import { useCallback } from 'react';

import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useScopedTranslate } from '@locale/translate';

import { useWebSocket } from 'api/hooks';
import { useInvalidateJourneyReviews } from 'api/journey-reviews/hooks';
import type { FindingSignature } from 'api/journey-reviews/requests';
import { handleError } from 'api/query-client';

import {
  ChatSessionResponse,
  PaginatedMessagesResponse,
  PaginationParams,
  RunAIBuilderResponse,
  ProviderStatusResponse,
  acceptPlan,
  answerQuestions,
  decideEnterPlanMode,
  deleteAIBuilderHistory,
  fixFindingWithNavigator,
  getProviderStatus,
  interruptAIBuilder,
  loadAIBuilderChatSession,
  loadAIBuilderExamplePrompts,
  loadAIBuilderMessages,
  loadAIBuilderNewMessages,
  pickResource,
  runAIBuilder,
  submitMarketplaceAuth,
  submitMessageFeedback,
  transcribeAudio,
  updateSessionMode,
} from './requests';

export const getAiBuilderMessagesQueryKey = (journeyShortId?: string) => [
  'ai-builder-messages',
  journeyShortId,
];

export const getAiBuilderSessionQueryKey = (journeyShortId?: string) => [
  'ai-builder-chat-session',
  journeyShortId,
];

export const useInvalidateAiBuilderSession = () => {
  const queryClient = useQueryClient();

  const invalidateSession = useCallback(
    (journeyShortId: string) => {
      return queryClient.invalidateQueries(getAiBuilderSessionQueryKey(journeyShortId));
    },
    [queryClient],
  );

  return { invalidateSession };
};

const useInvalidateAiBuilderMessages = () => {
  const queryClient = useQueryClient();

  const invalidateMessages = (journeyShortId: string) => {
    queryClient.invalidateQueries(getAiBuilderMessagesQueryKey(journeyShortId));
  };

  return {
    invalidateMessages,
  };
};

const PROVIDER_STATUS_QUERY_KEY = ['ai-builder-provider-status'];

export const useProviderStatus = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: PROVIDER_STATUS_QUERY_KEY,
    queryFn: getProviderStatus,
    enabled: options?.enabled,
  });
};

export const useProviderStatusWebSocket = (options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (event: MessageEvent<string>) => {
      if (!event?.data) return;
      const envelope = JSON.parse(event.data);
      if (envelope?.type !== 'update' || !envelope.payload?.status) return;

      queryClient.setQueryData(
        PROVIDER_STATUS_QUERY_KEY,
        (old: ProviderStatusResponse | undefined) => ({
          ...old,
          status: envelope.payload.status,
        }),
      );
    },
    [queryClient],
  );

  useWebSocket('ai-builder/provider-status/', handleMessage, {
    enabled: options?.enabled ?? true,
    throttleTime: 0,
  });
};

export const useRunAIBuilder = () => {
  return useMutation({
    mutationFn: runAIBuilder,
  });
};

export const useInterruptAIBuilder = () => {
  return useMutation({
    mutationFn: interruptAIBuilder,
  });
};

export const useAIBuilderMessagesInfinite = (
  journeyShortId: string,
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: getAiBuilderMessagesQueryKey(journeyShortId),
    queryFn: ({ pageParam }) =>
      loadAIBuilderMessages(journeyShortId, pageParam as PaginationParams | undefined),
    getNextPageParam: (lastPage: PaginatedMessagesResponse) =>
      lastPage.hasMore ? { beforeId: lastPage.oldestMessageId ?? undefined } : undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: options?.enabled,
  });
};

export const useAIBuilderChatSession = (
  journeyShortId: string,
  options?: Pick<
    UseQueryOptions<
      ChatSessionResponse,
      unknown,
      ChatSessionResponse,
      ReturnType<typeof getAiBuilderSessionQueryKey>
    >,
    'enabled' | 'refetchInterval'
  >,
) => {
  return useQuery({
    queryKey: getAiBuilderSessionQueryKey(journeyShortId),
    queryFn: () => loadAIBuilderChatSession(journeyShortId),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
    enabled: Boolean(journeyShortId) && (options?.enabled ?? true),
  });
};

type SessionPatch = Pick<ChatSessionResponse, 'shortId' | 'mode' | 'isGenerating'> &
  Partial<Pick<ChatSessionResponse, 'hasMessages'>>;

export const patchAiBuilderSession = (
  queryClient: QueryClient,
  journeyShortId: string,
  patch: SessionPatch,
) => {
  queryClient.setQueryData(
    getAiBuilderSessionQueryKey(journeyShortId),
    (old: ChatSessionResponse | undefined) => ({ ...old, ...patch }),
  );
};

const fixErrorMessage = (
  status: number | undefined,
  t: (key: keyof typeof locale.en) => string,
) => {
  if (status === 409) return t('navigatorBusy');
  if (status === 404) return t('findingGone');
  return t('fixNotSent');
};

export const useFixFindingWithNavigator = (
  journeyShortId: string,
  options?: UseMutationOptions<RunAIBuilderResponse, AxiosError, FindingSignature>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const { invalidateMessages } = useInvalidateAiBuilderMessages();
  const { invalidateSession } = useInvalidateAiBuilderSession();
  const { invalidateLatestJourneyReview } = useInvalidateJourneyReviews();
  const sessionQueryKey = getAiBuilderSessionQueryKey(journeyShortId);

  return useMutation<RunAIBuilderResponse, AxiosError, FindingSignature>({
    ...options,
    mutationFn: (signature) => fixFindingWithNavigator(journeyShortId, signature),
    onSuccess: async (...args) => {
      const [response] = args;
      // A session poll that started before the POST committed would otherwise
      // resolve after this write and put the idle state back.
      await queryClient.cancelQueries(sessionQueryKey);
      patchAiBuilderSession(queryClient, journeyShortId, {
        shortId: response.shortId,
        mode: response.mode,
        isGenerating: response.isGenerating,
        hasMessages: true,
      });
      invalidateMessages(journeyShortId);
      options?.onSuccess?.(...args);
    },
    onError: (...args) => {
      const [error] = args;
      const status = error.response?.status;
      handleError(fixErrorMessage(status, t));
      // The cached session can still read idle while another run holds the Navigator.
      if (status === 409) {
        invalidateSession(journeyShortId);
      }
      if (status === 404) {
        invalidateLatestJourneyReview(journeyShortId);
      }
      options?.onError?.(...args);
    },
  });
};

export const useAIBuilderExamplePrompts = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['ai-builder-example-prompts'],
    queryFn: loadAIBuilderExamplePrompts,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: options?.enabled,
  });
};

export const useAIBuilderNewMessages = () => {
  return useMutation({
    mutationFn: loadAIBuilderNewMessages,
  });
};

type DeleteAIBuilderHistoryParams = {
  journeyShortId: string;
};

export const useDeleteAIBuilderHistory = () => {
  return useMutation({
    mutationFn: ({ journeyShortId }: DeleteAIBuilderHistoryParams) =>
      deleteAIBuilderHistory(journeyShortId),
  });
};

export const useTranscribeAudio = () => {
  return useMutation({
    mutationFn: transcribeAudio,
  });
};

export const useAnswerQuestions = () => {
  return useMutation({
    mutationFn: answerQuestions,
  });
};

export const usePickResource = () => {
  return useMutation({
    mutationFn: pickResource,
  });
};

export const useUpdateSessionMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSessionMode,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        getAiBuilderSessionQueryKey(variables.journeyShortId),
        (old: ChatSessionResponse | undefined) => {
          if (!old) {
            return {
              shortId: data.shortId,
              mode: data.mode,
              isGenerating: false,
            };
          }
          return {
            ...old,
            shortId: data.shortId,
            mode: data.mode,
          };
        },
      );
    },
  });
};

export const useAcceptPlan = () => {
  const queryClient = useQueryClient();
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: acceptPlan,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId);
      queryClient.invalidateQueries(getAiBuilderSessionQueryKey(variables.journeyShortId));
    },
  });
};

export const useEnterPlanModeDecision = () => {
  const queryClient = useQueryClient();
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: decideEnterPlanMode,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId);
      queryClient.invalidateQueries(getAiBuilderSessionQueryKey(variables.journeyShortId));
    },
  });
};

export const useMarketplaceAuth = () => {
  const queryClient = useQueryClient();
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: submitMarketplaceAuth,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId);
      queryClient.invalidateQueries(getAiBuilderSessionQueryKey(variables.journeyShortId));
    },
  });
};

export const useSubmitMessageFeedback = (journeyShortId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: submitMessageFeedback,
    onMutate: async (variables) => {
      await queryClient.cancelQueries(getAiBuilderMessagesQueryKey(journeyShortId));

      const previousData = queryClient.getQueryData(getAiBuilderMessagesQueryKey(journeyShortId));

      queryClient.setQueryData(
        getAiBuilderMessagesQueryKey(journeyShortId),
        (old: InfiniteData<PaginatedMessagesResponse> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((message) =>
                message.id === variables.messageShortId
                  ? { ...message, feedbackRating: variables.feedbackRating }
                  : message,
              ),
            })),
          };
        },
      );

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          getAiBuilderMessagesQueryKey(journeyShortId),
          context.previousData,
        );
      }
    },
  });
};

export const locale = {
  en: {
    navigatorBusy: 'The Navigator is busy with another request. Try again once it finishes.',
    findingGone: 'This finding is no longer in the latest review. Run the review again.',
    fixNotSent: 'Could not send this finding to the Navigator. Try again.',
  },
};
